import { useState, useRef, useCallback } from 'react';

export function BeforeAfter({ before, after, beforeLabel = 'Avant', afterLabel = 'Après' }) {
  const [position, setPosition] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const updatePosition = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setPosition((x / rect.width) * 100);
  }, []);

  const onPointerDown = useCallback((e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    updatePosition(e.clientX);
  }, [updatePosition]);

  const onPointerMove = useCallback((e) => {
    if (!dragging.current) return;
    updatePosition(e.clientX);
  }, [updatePosition]);

  const onPointerUp = useCallback(() => {
    dragging.current = false;
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full rounded-2xl overflow-hidden border-2 border-border/50 shadow-xl select-none touch-none"
      style={{ aspectRatio: '4/3' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      data-testid="before-after-slider"
    >
      {/* After image (full background) */}
      {after ? (
        <img src={after} alt={afterLabel} className="absolute inset-0 w-full h-full object-cover object-top" draggable={false} />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center">
          <span className="text-orange-300 font-bold text-lg">Nouveau design à venir</span>
        </div>
      )}

      {/* Before image (clipped) */}
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      >
        <img src={before} alt={beforeLabel} className="absolute inset-0 w-full h-full object-cover object-top" draggable={false} />
      </div>

      {/* Slider line + handle */}
      <div
        className="absolute top-0 bottom-0 z-10 w-1 -ml-0.5 bg-white/90 shadow-[0_0_8px_rgba(0,0,0,0.3)]"
        style={{ left: `${position}%` }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center cursor-col-resize">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M5 3L2 8L5 13" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M11 3L14 8L11 13" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 z-20 pointer-events-none">
        <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-black/60 text-white backdrop-blur-sm">
          {beforeLabel}
        </span>
      </div>
      <div className="absolute top-4 right-4 z-20 pointer-events-none">
        <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-white/80 text-black backdrop-blur-sm">
          {afterLabel}
        </span>
      </div>
    </div>
  );
}
